// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A loading component for the accounting close run creation process.
 *
 * Displays a checklist of processing steps that animate sequentially
 * while the close run is being created on the backend. This component
 * must only be mounted when a close run is actively being created,
 * ensuring the animation starts fresh and timing is calculated correctly
 * based on the organization's asset count.
 *
 * Timing is dynamically calculated based on the organization's asset count:
 * - Network request: ~3,000 assets per second
 * - Schema parsing: ~2,400 assets per second
 *
 * Steps complete at timed intervals proportional to expected load time.
 */
import {
  ReportLoaderDisplay,
  closeRunConfig,
  useReportLoaderProgress,
} from '@/report/components/report-loader';

/**
 * Displays a pseudo-progress loader for close run creation.
 * Fetches asset count to calculate realistic step timing, then animates
 * through steps proportionally. Must be mounted fresh for each close run
 * to avoid stale animation state.
 */
export function CloseRunLoader() {
  const progress = useReportLoaderProgress({
    config: closeRunConfig,
  });

  return (
    <ReportLoaderDisplay
      currentStep={progress.currentStep}
      completedSteps={progress.completedSteps}
      stepLabels={closeRunConfig.stepLabels}
    />
  );
}
